/**
 * 카탈로그/테이블 뷰어/전역 검색 API.
 *
 * - admin tables 메타 + 범용 테이블 rows 조회 (28테이블 뷰어)
 * - 전체 카탈로그 검색 (11개 카탈로그 UNION ALL)
 * - 미등록 품목 인테이크(catalog_part_pending)
 */

import type {
  RowsResponse,
  SortOrder,
  TablesResponse,
} from '../types';
import { getJson, postJson } from './http';
import {
  TablesResponseSchema, RowsResponseSchema,
  CatalogSearchResponseSchema, PendingPartsResponseSchema,
  type PendingPart,
} from './schemas';
import type { LinkedProduct } from '@hcltech/shared/schemas/search.schema';

export type { PendingPart };

export async function fetchTables(): Promise<TablesResponse> {
  return getJson<TablesResponse>('/api/admin/tables', { schema: TablesResponseSchema });
}

export interface FetchRowsParams {
  table: string;
  search?: string;
  filters?: Record<string, string>;
  sort?: string | null;
  order?: SortOrder;
  limit?: number;
  offset?: number;
}

export async function fetchRows(
  params: FetchRowsParams,
): Promise<RowsResponse> {
  const sp = new URLSearchParams();
  if (params.search) sp.set('search', params.search);
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.offset != null) sp.set('offset', String(params.offset));
  if (params.filters) {
    for (const [col, val] of Object.entries(params.filters)) {
      if (val === '' || val == null) continue;
      sp.append(`filter[${col}]`, val);
    }
  }
  const qs = sp.toString();
  const path = `/api/admin/table/${encodeURIComponent(params.table)}${qs ? `?${qs}` : ''}`;
  return getJson<RowsResponse>(path, { schema: RowsResponseSchema });
}

// ─── 전체 카탈로그 검색 ─────────────────────────────────────────────────────

/** 검색 결과 1행 — source(테이블명) + option_part + extra_<col> 가변 컬럼 */
export interface CatalogSearchHit {
  source: string;
  option_part: string;
  description_raw?: string | null;   // 제조사 원문 — 검색 결과 표시용
  qty_available?: number;
  stock_condition?: string | null;   // FIFO 가용 재고 컨디션
  stock_cost_usd?: number | null;    // FIFO 참고 매입원가(USD)
  /** 이 행에 이어진 제품 — 1:1 이라 있으면 등록 폼이 다시 고르지 못한다. 없으면 null(2026-10-28, shared LinkedProductSchema) */
  linked_product: LinkedProduct | null;
  /** 동적 컬럼들 — extra_description, extra_model, extra_family 등 */
  [extraKey: string]: unknown;
}

export interface CatalogSearchResponse {
  q: string;
  catalog: CatalogSearchHit[];
  /** 전체 매칭 개수 (페이지 무관) — 페이저 표시용 */
  total: number;
  /** 현재 페이지 offset (백엔드 echo) */
  offset: number;
  /** 현재 페이지 limit (백엔드 echo) */
  limit: number;
  // inventory / quotes 는 두지 않는다.
  //   백엔드는 최상위 배열로 내려주지 않는다 — 재고는 각 catalog 행에
  //   per-row(qty_available·stock_condition·stock_cost_usd)로 붙는다.
  //   과거 로컬 복제본이 없는 배열을 요구해 dev 검색이 깨졌던 자리다
  //   (schemas.ts 의 CatalogSearchResponseSchema 주석 참조).
}

/**
 * GET /api/admin/search — 11개 카탈로그 UNION ALL + 서버 사이드 페이지네이션.
 *
 * @param q       검색어
 * @param offset  몇 번째 행부터 (페이지 × pageSize)
 * @param limit   한 페이지 크기 (서버 최대 500)
 */
export async function searchAll(
  q: string,
  offset = 0,
  limit = 50,
): Promise<CatalogSearchResponse> {
  const qs = new URLSearchParams({
    q,
    offset: String(offset),
    limit: String(limit),
  }).toString();
  return getJson<CatalogSearchResponse>(`/api/admin/search?${qs}`, { schema: CatalogSearchResponseSchema });
}

/**
 * 검색 결과 행 클릭 시 — 해당 카탈로그 테이블에서 그 option_part 의 full row 조회.
 * 드로어에 모든 컬럼을 표시하기 위함.
 */
export async function fetchCatalogRow(
  source: string,
  optionPart: string,
): Promise<Record<string, unknown> | null> {
  const sp = new URLSearchParams({
    search: optionPart,
    limit: '50',
  });
  const path = `/api/admin/table/${encodeURIComponent(source)}?${sp.toString()}`;
  const r = await getJson<RowsResponse>(path, { schema: RowsResponseSchema });
  // option_part 정확 일치하는 첫 row 반환 (검색은 ILIKE 라 같은 prefix 여러 행 가능)
  return (
    r.rows.find((row) => (row as { option_part?: string }).option_part === optionPart) ??
    null
  );
}

// ─── 미등록 품목 인테이크(catalog_part_pending) ─────────────────────
// PendingPart — 관문 z.infer 정본 (위 re-export 참조).
/** GET /api/admin/catalog-pending — 미등록 품목 목록(최신순). */
export async function fetchPendingParts(): Promise<PendingPart[]> {
  const body = await getJson<{ rows: PendingPart[] }>('/api/admin/catalog-pending', { schema: PendingPartsResponseSchema });
  return body.rows;
}

/**
 * POST /api/admin/catalog-pending — 임시(pending) 품목 등록.
 *
 * 서버 구성 편집기의 '품목 등록 → 등록 및 선택' 이 부른다. canonical 카탈로그는
 * 건드리지 않고 인테이크(catalog_part_pending)에만 적재한다.
 *
 * `category` 는 화면 어휘(CPU/RAM/…)를 그대로 보내면 된다 — 서버가 카탈로그
 * 소스명(cpu/memory/…)으로 변환해 저장한다. 어휘 규칙을 한 곳(백엔드)에만 둔다.
 *
 * 같은 파트넘버가 이미 있으면 새 행을 만들지 않고 비어 있는 칸만 채운다
 * (서버의 ON CONFLICT ... COALESCE).
 */
export async function createPendingPart(input: {
  option_part:     string;
  description?:    string | null;
  category?:       string | null;
  source_deal_id?: string | null;
}): Promise<void> {
  await postJson('/api/admin/catalog-pending', input);
}
